import { useState } from 'react';
import { UMButton, UMIcon } from '../../urbanmap/_components';
import { DegreeField, DegreePair, DegreePairFields } from './DegreeFields';
import { PerfTable } from './PerfTable';
import { RatioImport } from './RatioImport';
import { RatioSummaryTable } from './RatioSummaryTable';
import { countModifiedRatios, isValidDjclim, isValidDju } from './domain';
import { parseNum } from './sheet';
import { PC, UMR, caption, card } from './tokens';
import type { DegreeDays, RatioTable as Ratios } from './types';

interface Props {
  /** Degrés-jours auxquels mes ratios ont été établis — sans lien avec la station du projet. */
  degrees: DegreeDays;
  ratios: Ratios;
  /** Référentiel de démonstration, proposé par défaut pour aider à la saisie. */
  defaults: Ratios;
  defaultDegrees: DegreeDays;
  onDegreesChange: (next: DegreeDays) => void;
  onRatiosChange: (next: Ratios) => void;
  onValidityChange: (ok: boolean) => void;
  /** Mode saisie remonté au parent : il désactive l'application au projet. */
  onEditingChange: (editing: boolean) => void;
}

/** Encart « Mes ratios » : import d'un fichier ou saisie manuelle, DJ et ratios modifiables ensemble. */
export function MyRatiosCard(p: Props) {
  const [editing, setEditing] = useState(false);
  const [dju, setDju] = useState(String(p.degrees.dju));
  const [djclim, setDjclim] = useState(String(p.degrees.djclim));
  const [summaryOk, setSummaryOk] = useState(true);
  const [sheetOk, setSheetOk] = useState(true);
  const [perfOk, setPerfOk] = useState(true);

  const djuValid = isValidDju(parseNum(dju));
  const climValid = isValidDjclim(parseNum(djclim));
  const valid = summaryOk && sheetOk && perfOk && djuValid && climValid;
  const modified = countModifiedRatios(p.ratios, p.defaults);
  const djChanged = p.degrees.dju !== p.defaultDegrees.dju || p.degrees.djclim !== p.defaultDegrees.djclim;
  const setMode = (on: boolean) => { setEditing(on); p.onEditingChange(on); };

  const emit = (summary: boolean, sheet: boolean, perf = perfOk) => {
    setSummaryOk(summary); setSheetOk(sheet); setPerfOk(perf);
    p.onValidityChange(summary && sheet && perf && djuValid && climValid);
  };
  const open = () => {
    setDju(String(p.degrees.dju)); setDjclim(String(p.degrees.djclim)); setMode(true);
  };
  const editDju = (v: string) => {
    setDju(v);
    const n = parseNum(v);
    if (isValidDju(n)) p.onDegreesChange({ ...p.degrees, dju: n });
    p.onValidityChange(summaryOk && sheetOk && perfOk && isValidDju(n) && climValid);
  };
  const editDjclim = (v: string) => {
    setDjclim(v);
    const n = parseNum(v);
    if (isValidDjclim(n)) p.onDegreesChange({ ...p.degrees, djclim: n });
    p.onValidityChange(summaryOk && sheetOk && perfOk && djuValid && isValidDjclim(n));
  };
  const imported = (degrees: DegreeDays, ratios: Ratios) => {
    p.onDegreesChange(degrees); p.onRatiosChange(ratios);
    setDju(String(degrees.dju)); setDjclim(String(degrees.djclim));
  };
  const resetAll = () => {
    p.onDegreesChange(p.defaultDegrees); p.onRatiosChange(p.defaults);
    setDju(String(p.defaultDegrees.dju)); setDjclim(String(p.defaultDegrees.djclim));
    emit(true, true, true);
  };

  return (
    <section style={{ ...card, padding: 0, display: 'flex', flexDirection: 'column' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: UMR.grey100, borderBottom: `1px solid ${UMR.border}` }}>
        <span style={{ width: 22, height: 22, background: UMR.white, border: `1px solid ${UMR.border}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>1</span>
        <UMIcon name="tune" size={20} color={PC.accent} />
        <strong style={{ fontSize: 15 }}>Mes ratios</strong>
        {modified > 0 && (
          <span style={{ fontSize: 12, padding: '1px 6px', background: PC.accentBg, color: PC.accentStrong, border: `1px solid ${PC.accentBorder}` }}>
            {modified} valeur{modified > 1 ? 's' : ''} modifiée{modified > 1 ? 's' : ''}
          </span>
        )}
        <span style={{ ...caption, flex: 1, minWidth: 200 }}>
          Saisissez ou importez vos propres ratios, ou appliquez les ratios par défaut de la zone climatique de votre projet.
        </span>
      </header>

      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Actions : une seule façon de faire à la fois, au-dessus des tableaux qu'elles modifient. */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {editing ? (
            <>
              <UMButton icon="check" disabled={!valid} onClick={() => setMode(false)}>Valider mes ratios et coefficients</UMButton>
              <UMButton variant="subtle" icon="undo" onClick={resetAll}>Valeurs par défaut</UMButton>
              {!valid && <span style={{ ...caption, color: PC.dangerText }}>Corrigez les valeurs hors plage avant de valider.</span>}
            </>
          ) : (
            <>
              <UMButton variant="secondary" icon="edit" onClick={open}>Modifier mes ratios et coefficients</UMButton>
              <RatioImport degrees={p.degrees} ratios={p.ratios} onImport={imported} compact />
              {(modified > 0 || djChanged) && <UMButton variant="subtle" icon="undo" onClick={resetAll}>Valeurs par défaut</UMButton>}
            </>
          )}
        </div>

        {/* Degrés-jours de mes ratios, groupés à gauche. */}
        {editing ? (
          <DegreePairFields>
            <DegreeField label="DJU (chauffage)" text={dju} valid={djuValid} range="500 à 5 000" onChange={editDju} />
            <DegreeField label="DJClim (froid)" text={djclim} valid={climValid} range="10 à 1 000" onChange={editDjclim} />
          </DegreePairFields>
        ) : (
          <DegreePair dju={p.degrees.dju} djclim={p.degrees.djclim} hint="Degrés-jours de mes ratios" highlight={djChanged} />
        )}

        {/* Mes ratios par typologie et coefficients de performance, à hauteur égale. */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'stretch' }}>
          <div style={{ flex: '0 1 680px', minWidth: 560, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <strong style={{ fontSize: 13 }}>Ratios de la période de référence</strong>
            <div style={{ flex: 1, display: 'flex', border: `1px solid ${UMR.border}`, background: UMR.white }}>
              <RatioSummaryTable
                value={p.ratios}
                defaults={p.defaults}
                editing={editing}
                showTotal={false}
                onChange={p.onRatiosChange}
                onValidityChange={(ok) => emit(ok, sheetOk)}
              />
            </div>
          </div>
          <div style={{ flex: '1 1 520px', minWidth: 460, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <strong style={{ fontSize: 13 }}>Coefficients de performance</strong>
            <div style={{ flex: 1, display: 'flex', border: `1px solid ${UMR.border}`, background: UMR.white }}>
              <PerfTable
                value={p.ratios}
                defaults={p.defaults}
                editing={editing}
                onChange={p.onRatiosChange}
                onValidityChange={(ok) => emit(summaryOk, sheetOk, ok)}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
